import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Bell, LogOut, User } from 'lucide-react'
import { apiGet, logout } from '@/lib/api'
import { qk } from '@/lib/query-client'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const ROLE_LABELS: Record<string, string> = {
  ADMIN:    'Admin',
  HR:       'RH',
  MANAGER:  'Gestor',
  EMPLOYEE: 'Colaborador',
}

interface HeaderProps {
  user: {
    name: string
    email: string
    role: string
  }
}

export function Header({ user }: HeaderProps) {
  // Contador do sino. Sob TanStack Query em vez do useEffect original: a
  // página de notificações invalida `qk.unreadCount` ao marcar como lida,
  // então o badge atualiza sem reload.
  const { data: unread = 0 } = useQuery({
    queryKey: qk.unreadCount,
    queryFn: () =>
      apiGet<{ unreadCount?: number }>('/api/notifications?unread=true&limit=1').then(
        d => d.unreadCount ?? 0,
      ),
    // Falha aqui é cosmética — não vale poluir a tela com erro.
    retry: false,
    staleTime: 60_000,
  })

  const roleLabel = ROLE_LABELS[user.role] ?? user.role
  const initials  = user.name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase()

  return (
    <header
      className="h-16 flex items-center justify-between px-6 flex-shrink-0"
      style={{ background: '#FFFFFF', borderBottom: '1px solid #ECE8DC' }}
    >
      <div />

      <div className="flex items-center gap-2">
        {/* Sino de notificações */}
        <Link
          to="/notificacoes"
          title="Notificações"
          className="relative inline-flex items-center justify-center size-9 rounded-lg transition-colors text-[#6B6A64]"
          style={{ border: '1px solid transparent' }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.background = '#F7F3EA'
            ;(e.currentTarget as HTMLElement).style.color = '#9A7D43'
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.background = 'transparent'
            ;(e.currentTarget as HTMLElement).style.color = '#6B6A64'
          }}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-[#BE123C] rounded-full flex items-center justify-center text-[10px] font-bold text-white px-0.5">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Link>

        {/* Menu do usuário */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex items-center gap-2.5 h-9 px-3 rounded-full transition-colors outline-none"
            style={{ border: '1px solid #ECE8DC' }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.borderColor = '#BC9C5D'
              ;(e.currentTarget as HTMLElement).style.background = '#F7F3EA'
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.borderColor = '#ECE8DC'
              ;(e.currentTarget as HTMLElement).style.background = 'transparent'
            }}
          >
            {/* Avatar com iniciais em gold */}
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
              style={{ background: '#BC9C5D', color: '#1A1A1A' }}
            >
              {initials || <User className="w-3 h-3" />}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-xs font-semibold leading-none" style={{ color: '#1A1A1A' }}>
                {user.name.split(' ')[0]}
              </p>
              <p className="text-[10px] mt-0.5 uppercase tracking-wide" style={{ color: '#6B6A64' }}>
                {roleLabel}
              </p>
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>
              <p className="font-semibold text-sm">{user.name}</p>
              <p className="text-xs font-normal mt-0.5" style={{ color: '#6B6A64' }}>{user.email}</p>
              <p className="text-[10px] font-normal mt-0.5 uppercase tracking-wide" style={{ color: '#BC9C5D' }}>
                {roleLabel}
              </p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-[#BE123C] cursor-pointer"
              onClick={() => logout()}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
